import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box } from "@mui/material";
import { PropTypes } from "prop-types";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AutoCompleteMenu, Calendar, Loader } from "@/atoms";
import { FormContainer } from "@/components";
import {
    constantsData,
    distributionTypeFormData,
    inputData,
    releaseVideoDistributionFormData,
    selectionsData,
} from "@/data";
import {
    useClientsList,
    useCreateVideoDistribution,
    useDistributionFileTypesSelectionList,
    useReleasesVideosSelectionList,
    useTerritoriesList,
} from "@/hooks";
import { useReleaseVideoDistributionSchema } from "@/schemas";
import { renderFormControllerHandler } from "@/utils";

const ALL_VIDEOS_OPTION_VALUE = "all";

const ReleaseVideoDistributionForm = ({
    action,
    cancelAction,
    distributionType,
    hasBulkDistributions,
    hasRefetchDistributions,
    limit,
    page,
    releasesIds,
    setBulkResults,
    setDistributionType,
}) => {
    const { t } = useTranslation();

    const schema = useReleaseVideoDistributionSchema(hasBulkDistributions);

    const selections = useSelector((state) => state.selectionsReducer);

    const formMethods = useForm({
        defaultValues: {
            distributionType,
            videos: [],
        },
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const {
        control,
        formState: { errors },
    } = formMethods;

    // unpaginated and narrowed server-side to clients configured for video distribution
    const { fetched: clientsFetched, loading: clientsLoading } = useClientsList(
        false,
        true,
        null,
        true,
        constantsData.videoDistributionType,
    );

    const { loading: distributionFileTypesLoading } = useDistributionFileTypesSelectionList();

    const {
        count: territoriesCount,
        fetched: territoriesFetched,
        loading: territoriesLoading,
        page: territoriesPage,
        refetch: territoriesRefetch,
        searchKey: territoriesSearchKey,
    } = useTerritoriesList(true, true, null, true);

    const { loading: videosLoading, options: videosOptions } = useReleasesVideosSelectionList(
        hasBulkDistributions ? [] : releasesIds,
    );

    const { createVideoDistribution, loading: createVideoDistributionLoading } = useCreateVideoDistribution(
        releasesIds,
        hasBulkDistributions,
        hasRefetchDistributions,
        limit,
        page,
        setBulkResults,
    );

    const {
        client: clientInputName,
        distributionType: distributionTypeInputName,
        videos: videosInputName,
    } = inputData.names;

    const asyncMenus = {
        territories: {
            count: territoriesCount,
            loading: territoriesLoading,
            page: territoriesPage,
            refetch: territoriesRefetch,
            searchKey: territoriesSearchKey,
            selectionKey: selectionsData.keys.territories,
        },
    };

    const videoDistributionClientsOptions = selections?.[selectionsData.keys.videoDistributionClients] || [];

    // the video XML template is always rendered/delivered as an "xml" distribution, matching the
    // validation in distributeValidation() that requires the client's treeObj for that type
    const xmlDistributeTypeId = selections?.[selectionsData.keys.distributionFileTypes]?.find(
        ({ label }) => label?.toLowerCase() === "xml",
    )?.value;

    const allVideosOption = {
        label: t("labels.allVideos"),
        value: ALL_VIDEOS_OPTION_VALUE,
    };

    // bulk has no video picker: every video of every selected release goes out
    const finalizedFieldsArray = [
        distributionTypeFormData,
        ...(hasBulkDistributions
            ? releaseVideoDistributionFormData.filter(({ name }) => name !== videosInputName)
            : releaseVideoDistributionFormData),
    ];

    // "All Videos" is resolved by the menu itself into every video, so the form only ever holds real videos
    const submitFormHandler = (values) =>
        createVideoDistribution({
            ...values,
            distributeTypeId: xmlDistributeTypeId,
        });

    const renderFormControllerChildrenHandler = (field, label, name, type, isOptional, selectionKey, isAsyncMenu) => {
        switch (type) {
            case inputData.types.date:
                return (
                    <Calendar
                        errors={errors}
                        field={field}
                        isOptional={isOptional}
                        label={label}
                        name={name}
                        value={field.value || null}
                        hasTime
                    />
                );

            case inputData.types.multiAutoComplete:
                return (
                    <AutoCompleteMenu
                        defaultValue={[]}
                        errors={errors}
                        getRenderedOption={(option) => option?.label}
                        label={label}
                        loading={videosLoading}
                        name={name}
                        optionLabel={(option) => option.label}
                        options={videosOptions}
                        selectAllOption={allVideosOption}
                        isMultiple
                        onChange={(_, options) => field.onChange(options || [])}
                    />
                );

            default:
                return (
                    <AutoCompleteMenu
                        asyncMenuData={asyncMenus?.[selectionKey]}
                        errors={errors}
                        getRenderedOption={(option) => option.label}
                        isAsyncMenu={isAsyncMenu}
                        label={label}
                        name={name}
                        optionLabel={(option) => option.label}
                        defaultValue={
                            name === distributionTypeInputName
                                ? selections?.[selectionKey]?.find(({ value }) => value === distributionType)
                                : undefined
                        }
                        options={
                            name === clientInputName ? videoDistributionClientsOptions : selections?.[selectionKey]
                        }
                        onChange={(_, option) => {
                            if (name === distributionTypeInputName && option) setDistributionType(option.value);

                            if (option) field.onChange(option.value);
                            else field.onChange("");
                        }}
                    />
                );
        }
    };

    if (
        (clientsLoading && !clientsFetched) ||
        (territoriesLoading && !territoriesFetched) ||
        distributionFileTypesLoading
    )
        return <Loader withoutFullHeight />;

    return (
        <Box>
            {hasBulkDistributions && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        mx: 2,
                    }}
                >
                    {t("messages.distributionMsgs.video.bulkAllVideos", { releases: releasesIds?.length || 0 })}
                </Alert>
            )}
            {!hasBulkDistributions && videosOptions.length === 0 && !videosLoading && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        mx: 2,
                    }}
                >
                    {t("messages.distributionMsgs.video.noVideos")}
                </Alert>
            )}
            <FormContainer
                action={action}
                cancelAction={cancelAction}
                disabled={!hasBulkDistributions && videosOptions.length === 0}
                formMethods={formMethods}
                loading={createVideoDistributionLoading}
                data={renderFormControllerHandler(control, finalizedFieldsArray, renderFormControllerChildrenHandler)}
                hasCancelButton
                withoutShadow
                onSubmitForm={submitFormHandler}
            />
        </Box>
    );
};

export default ReleaseVideoDistributionForm;

ReleaseVideoDistributionForm.propTypes = {
    action: PropTypes.string,
    cancelAction: PropTypes.func,
    distributionType: PropTypes.string,
    hasBulkDistributions: PropTypes.bool,
    hasRefetchDistributions: PropTypes.bool,
    limit: PropTypes.number,
    page: PropTypes.number,
    releasesIds: PropTypes.array,
    setBulkResults: PropTypes.func,
    setDistributionType: PropTypes.func,
};
